import type React from 'react'
import { useCallback, useMemo } from 'react'

import type { Row } from '@tanstack/react-table'
import { useUnit } from 'effector-react'
import { useNavigate } from 'react-router'

import EditIcon from '@/assets/icons/icon-edit.svg'
import RemoveIcon from '@/assets/icons/icon-trash.svg'
import { Checkbox } from '@/components/ui/checkbox'
import type {
  Entry,
  FileEntry,
} from '@/components/ui/table/expandable-dnd-table/useTreeTable'
import { DIALOG_TYPE } from '@/dialogs/constants.ts'
import { $creatorId } from '@/models/creators.ts'
import { openDialog } from '@/models/dialogs.ts'
import { IconButton } from '@/modules/chatting/ui/components/icon-button'
import type { ScriptNode } from '@/modules/scripts/types'
import { AppRoutes } from '@/utils/constants.ts'

import type { SelectedItemsMap } from './ScriptView'

const isFileEntry = (
  entry: Entry<ScriptNode>,
): entry is FileEntry<ScriptNode> => entry._type === 'file'

interface ActionButtonsProps {
  row: Row<Entry<ScriptNode>>
  selectedItems: SelectedItemsMap
  setSelectedItems: React.Dispatch<React.SetStateAction<SelectedItemsMap>>
}

export const ActionButtons = ({
  row,
  selectedItems,
  setSelectedItems,
}: ActionButtonsProps) => {
  const rowId = row.original.id
  const rowName = row.original.name
  const isUncategorized = rowId === 0

  const navigate = useNavigate()
  const creatorId = useUnit($creatorId)
  const isRowCategory = row.original._type === 'folder'

  const openRemoveDialog = () => {
    openDialog({
      type: DIALOG_TYPE.REMOVE_SCRIPTS_FOLDER,
      row: row,
      onRemove: handleRemove,
    })
  }

  const openEditDialog = () => {
    openDialog({
      type: DIALOG_TYPE.EDIT_SCRIPTS_FOLDER,
      rowId,
      prevName: rowName,
    })
  }

  const onEditIconClick = () => {
    if (isRowCategory) {
      openEditDialog()

      return
    }
    navigate(
      AppRoutes.Creator.Settings.Scripts.Edit.asUrl(
        String(creatorId),
        String(row.original.id),
      ),
    )
    //Editing script logic here
  }

  const toggleSelection = useCallback(() => {
    const newSelectedItems = new Map(selectedItems)
    const isFolder = row.original._type === 'folder'
    const toggleItem = (
      item: Entry<ScriptNode>,
      folder: Map<ScriptNode['id'], ScriptNode>,
    ) => {
      if (folder.has(item.id)) {
        folder.delete(item.id)
      } else {
        folder.set(item.id, item)
      }
    }

    if (!newSelectedItems.get(0)) {
      newSelectedItems.set(0, new Map())
    }
    if (isFolder) {
      if (newSelectedItems.has(row.original.id)) {
        newSelectedItems.delete(row.original.id)
      } else {
        newSelectedItems.set(row.original.id, new Map())
        ;(row.original.scripts ?? []).forEach((child) => {
          if (newSelectedItems.get(0)?.get(child.id)) {
            newSelectedItems.get(0)?.delete(child.id)
            newSelectedItems.get(row.original.id)?.set(child.id, child)
          }
        })
        if (newSelectedItems.get(row.original.id)?.size === 0) {
          ;(row.original.scripts ?? []).forEach((child) => {
            newSelectedItems.get(row.original.id)?.set(child.id, child)
          })
        }
      }
    } else if (isFileEntry(row.original)) {
      const parent = row.original._parent()

      if (parent && newSelectedItems.get(parent.id)) {
        toggleItem(row.original, newSelectedItems.get(parent.id)!)
      } else {
        toggleItem(row.original, newSelectedItems.get(0)!)
      }
    }

    if (newSelectedItems.get(0)?.size === 0) {
      newSelectedItems.delete(0)
    }

    setSelectedItems(newSelectedItems)
  }, [selectedItems, setSelectedItems, row.original])

  const isChecked = useMemo(() => {
    if (isRowCategory) {
      return selectedItems.has(row.original.id)
    } else if (isFileEntry(row.original)) {
      const parent = row.original._parent()

      if (!parent) {
        return false
      }

      return (
        selectedItems.get(parent.id)?.has(row.original.id) ??
        selectedItems.get(0)?.has(row.original.id) ??
        false
      )
    }
  }, [selectedItems, isRowCategory, row.original])

  const handleRemove = useCallback(() => {
    if (isChecked) {
      toggleSelection()
    }
  }, [isChecked, toggleSelection])

  return isUncategorized ? null : (
    <div className="float-right flex gap-4 items-center">
      <Checkbox
        checked={isChecked}
        onClick={toggleSelection}
        className="cursor-pointer text-primary-500"
      />
      <IconButton
        tooltip={isRowCategory ? 'Edit folder' : 'Edit script'}
        icon={EditIcon}
        onClick={onEditIconClick}
        iconClassName="h-5 w-5"
        className="cursor-pointer text-primary-500"
        testId="edit-script-button"
      />
      <IconButton
        tooltip={isRowCategory ? 'Remove folder' : 'Remove script'}
        icon={RemoveIcon}
        iconClassName="h-5 w-5"
        onClick={openRemoveDialog}
        className="cursor-pointer text-danger-default"
        testId="remove-script-button"
      />
    </div>
  )
}
